/**
 * Is this restaurant's chat switched on? (R7)
 *
 * Answers a TENANT question, so it needs no signed-in customer and is safe
 * to call at boot. Cached per session because the answer changes when a
 * restaurant changes plan or flips a setting — neither of which happens
 * while someone is browsing a menu.
 *
 * Fails to `false`. If we cannot tell, we do not show a Chat button: an
 * entry point that leads to a dead end is worse than no entry point, and
 * the server is the only thing that knows the answer.
 */
import { useEffect, useState } from "react";
import { chatApi } from "@/api/chat";

let cached: boolean | null = null;
let inFlight: Promise<boolean> | null = null;

function resolveAvailability(): Promise<boolean> {
  if (cached !== null) return Promise.resolve(cached);
  if (inFlight) return inFlight;
  inFlight = chatApi
    .availability()
    .then((res) => {
      cached = Boolean(res?.enabled);
      return cached;
    })
    .catch(() => {
      // Deliberately NOT cached: a transient failure at boot should not hide
      // chat for the rest of the session.
      inFlight = null;
      return false;
    });
  return inFlight;
}

export function useChatAvailability(): boolean {
  const [enabled, setEnabled] = useState<boolean>(cached ?? false);

  useEffect(() => {
    let cancelled = false;
    void resolveAvailability().then((value) => {
      if (!cancelled) setEnabled(value);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return enabled;
}

/** Test-only: forget the session cache between cases. */
export function resetChatAvailabilityForTests(): void {
  cached = null;
  inFlight = null;
}
